import { useEffect, useMemo } from "react";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { useTranslation } from "react-i18next";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";

import { createAppTheme } from "../config/muiTheme";
import getDirection from "../i18n/getDirection";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from "../i18n/languages";

const ltrCache = createCache({ key: "mui" });
const rtlCache = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin] });

const AppThemeProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const activeLanguage = SUPPORTED_LANGUAGES.includes(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : SUPPORTED_LANGUAGES.includes(i18n.language)
      ? i18n.language
      : DEFAULT_LANGUAGE;

  const direction = getDirection(activeLanguage);
  const cache = direction === "rtl" ? rtlCache : ltrCache;
  const theme = useMemo(
    () => createAppTheme({ direction, language: activeLanguage }),
    [direction, activeLanguage]
  );

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = activeLanguage;
      document.documentElement.dir = direction;
      document.body.dir = direction;
    }

    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, activeLanguage);
      } catch {
        // ignore localStorage write issues
      }
    }
  }, [activeLanguage, direction]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
};

export default AppThemeProvider;
